import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import GetData from "../../services/GetData.js";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import api from "../../services/axiosInterceptor.js";
import ConfirmDeleteDialog from "../Helpers/ConfirmDeleteDialog.jsx";

/**
 * Add, rename, or remove choices for one SKU single-select.
 * Renames update products that use the old name. Removing a choice clears it on those products.
 */
export default function SelectOptionsDialog({
    open,
    fieldKey,
    fieldLabel,
    onClose,
    onChanged,
}) {
    const [options, setOptions] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [newName, setNewName] = useState("");
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState("");
    const [error, setError] = useState("");
    const [pendingRemove, setPendingRemove] = useState(null);

    const load = async () => {
        if (!fieldKey) return;
        setLoading(true);
        setError("");
        const { getData } = await GetData(`broylesllc/product-selects/${fieldKey}`);
        setLoading(false);
        if (!getData?.options) {
            setError("Could not load options.");
            return;
        }
        setOptions(getData.options);
        setDrafts(
            Object.fromEntries(getData.options.map((option) => [option.id, option.label]))
        );
    };

    useEffect(() => {
        if (!open) return;
        setNewName("");
        load();
    }, [open, fieldKey]);

    const messageFrom = (result) =>
        result?.data?.message ||
        result?.error?.response?.data?.message ||
        "Could not save that option.";

    const addOption = async () => {
        const label = newName.trim();
        if (!label) return;
        setBusyId("new");
        setError("");
        const result = await PostData({ label }, `broylesllc/product-selects/${fieldKey}`);
        setBusyId("");
        if (!result.success) {
            setError(messageFrom(result));
            return;
        }
        setNewName("");
        await load();
        onChanged?.({ type: "add", label: result.data?.label || label });
    };

    const renameOption = async (option) => {
        const label = String(drafts[option.id] || "").trim();
        if (!label || label === option.label) return;
        setBusyId(option.id);
        setError("");
        const result = await PutData(
            { label },
            `broylesllc/product-selects/${fieldKey}/${option.id}`
        );
        setBusyId("");
        if (!result.success) {
            setError(messageFrom(result));
            return;
        }
        await load();
        onChanged?.({ type: "rename", previous: option.label, label });
    };

    const removeOption = async () => {
        const option = pendingRemove;
        if (!option) return;
        setBusyId(option.id);
        setError("");
        try {
            await api.delete(`api/broylesllc/product-selects/${fieldKey}/${option.id}`);
            setPendingRemove(null);
            await load();
            onChanged?.({ type: "remove", label: option.label });
        } catch (err) {
            setError(err?.response?.data?.message || "Could not remove that option.");
        } finally {
            setBusyId("");
        }
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>
                {fieldLabel ? `${fieldLabel} options` : "Select options"}
            </DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ fontSize: 13, color: "#64748B", mb: 1.5 }}>
                    Add a choice, rename it, or remove it. Renaming updates SKUs that use it.
                    Removing clears that choice on those SKUs.
                </Typography>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={22} />
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {options.map((option) => (
                            <Box key={option.id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    hiddenLabel
                                    value={drafts[option.id] ?? ""}
                                    onChange={(event) =>
                                        setDrafts((prev) => ({
                                            ...prev,
                                            [option.id]: event.target.value,
                                        }))
                                    }
                                    onBlur={() => renameOption(option)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            renameOption(option);
                                        }
                                    }}
                                />
                                <IconButton
                                    aria-label={`Remove ${option.label}`}
                                    disabled={busyId === option.id}
                                    onClick={() => setPendingRemove(option)}
                                    size="small"
                                >
                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        {!options.length ? (
                            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                                No options yet.
                            </Typography>
                        ) : null}
                    </Box>
                )}
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <TextField
                        size="small"
                        fullWidth
                        hiddenLabel
                        placeholder="New option"
                        value={newName}
                        onChange={(event) => setNewName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                addOption();
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        disabled={!newName.trim() || busyId === "new"}
                        onClick={addOption}
                        sx={{ textTransform: "none", fontWeight: 700, boxShadow: "none" }}
                    >
                        Add
                    </Button>
                </Box>
                {error ? (
                    <Typography color="error" sx={{ mt: 1.5, fontSize: 13 }}>
                        {error}
                    </Typography>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>
                    Done
                </Button>
            </DialogActions>
        </Dialog>
        <ConfirmDeleteDialog
            open={Boolean(pendingRemove)}
            onClose={() => setPendingRemove(null)}
            onConfirm={removeOption}
            confirming={busyId === pendingRemove?.id}
            title="Remove option?"
            description="Removing clears this choice on SKUs that use it."
            items={
                pendingRemove
                    ? [{ key: pendingRemove.id, label: pendingRemove.label }]
                    : []
            }
            confirmLabel="Remove"
        />
        </>
    );
}
